/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/bolmateMultiSelect.scss';

import React, {
    CSSProperties,
    ForwardedRef,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import {AwesomeIcon, ErrorType, hasHover} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import {COLOR_BLUE_BOLMATE} from '../../../util/constants';
import {BolmateSelectItem, BolmateSelectItemValue} from '../../../util/interfaces';
import {BolmateSelectRef} from './bolmateSelect';

interface Props<RT = string | number> {
    onChange: (values: Array<BolmateSelectItemValue<RT>>) => void;
    values: Array<BolmateSelectItemValue<RT>>;
    items: Array<BolmateSelectItem<RT>>;
    disabled?: boolean;
    error?: ErrorType;
    label?: string;
    placeholder?: string;
    selectedLabel?: string;
    selectAllLabel?: string;
    className?: string;
    labelClassName?: string;
    valueClassName?: string;
    arrowClassName?: string;
    errorClassName?: string;
    itemContainerClassName?: string;
    itemClassName?: string;
    itemsPosition?: 'top' | 'bottom';
    itemsHeight?: number | string;
    active?: boolean;
}

function BolmateMultiSelect<RT = string | number>(props: Props<RT>, ref: ForwardedRef<BolmateSelectRef>) {
    const {
        disabled,
        onChange,
        values,
        items,
        active,
        label,
        placeholder,
        selectedLabel,
        selectAllLabel,
        labelClassName,
        valueClassName,
        arrowClassName,
        itemsHeight,
        itemsPosition,
        itemContainerClassName,
        itemClassName,
        error,
        errorClassName,
        className
    } = props;
    const [open, setOpen] = useState(false);
    const container = useRef<HTMLDivElement>(null);
    const selectableItems = useMemo(() => items.filter(item => item.selectable !== false), [items]);
    const allSelected = useMemo(() => {
        if (selectableItems.length === 0) return false;
        return selectableItems.every(item => values.includes(item.value));
    }, [values, selectableItems]);
    const contentStyle = useMemo(() => {
        const style: CSSProperties = {};
        if (active) style.border = `2px solid ${COLOR_BLUE_BOLMATE}`;
        return style;
    }, [active]);

    function onClickElement() {
        if (disabled) return;
        setOpen(!open);
    }

    function onToggleItem(value: BolmateSelectItemValue<RT>) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            if (values.includes(value)) onChange(values.filter(v => v !== value));
            else onChange([...values, value]);
        };
    }

    function onToggleAll(evt: React.MouseEvent) {
        evt.stopPropagation();
        if (allSelected) onChange([]);
        else onChange(selectableItems.map(item => item.value));
    }

    function onOpen() {
        setOpen(true);
    }

    function isOpen(): boolean {
        return open;
    }

    function renderLabel() {
        if (!label) return null;
        return (
            <label className={classNames('bolmate-multi-select-label', labelClassName)} onClick={onClickElement}>
                {label}
            </label>
        );
    }

    function getValueLabel(): string {
        if (values.length === 0) return placeholder ?? '';
        if (!selectedLabel) return placeholder ?? '';
        if (allSelected) return `Alle ${selectedLabel}`;
        return `${values.length} ${selectedLabel}`;
    }

    function renderValue() {
        return (
            <label className={classNames('bolmate-multi-select-value', valueClassName)} onClick={onClickElement}>
                {getValueLabel()}
            </label>
        );
    }

    function renderArrow() {
        return (
            <AwesomeIcon
                name='chevron-down'
                onClick={onClickElement}
                className={classNames('bolmate-multi-select-arrow', open && 'open', arrowClassName)}
            />
        );
    }

    function renderCheckbox(checked: boolean) {
        return (
            <span className={classNames('bolmate-multi-select-checkbox', checked && 'checked')}>
                {checked && <AwesomeIcon name='check' />}
            </span>
        );
    }

    function renderSelectAll() {
        if (!selectAllLabel) return null;
        return (
            <label className={classNames('bolmate-multi-select-item select-all', itemClassName)} onClick={onToggleAll}>
                {renderCheckbox(allSelected)}
                <span>{selectAllLabel}</span>
            </label>
        );
    }

    function renderItems() {
        let style: CSSProperties | undefined = undefined;
        if (itemsHeight !== undefined) {
            let height: string;
            if (typeof itemsHeight === 'number') height = itemsHeight + 'px';
            else height = itemsHeight;
            style = {maxHeight: height, overflowY: 'auto'};
        }
        return (
            <div
                style={style}
                className={classNames(
                    'bolmate-multi-select-items',
                    open && !disabled && 'open',
                    itemsPosition ?? 'bottom',
                    itemContainerClassName
                )}
            >
                {renderSelectAll()}
                {selectableItems.map(renderItem)}
            </div>
        );
    }

    function renderItem(item: BolmateSelectItem<RT>, index: number) {
        const {label, value} = item;
        const checked = values.includes(value);
        return (
            <label
                key={index}
                className={classNames('bolmate-multi-select-item', checked && 'checked', itemClassName)}
                onClick={onToggleItem(value)}
            >
                {renderCheckbox(checked)}
                <span>{['string', 'number'].includes(typeof label) ? label : String(value)}</span>
            </label>
        );
    }

    function renderSizer() {
        return (
            <div className='bolmate-multi-select-sizer' aria-hidden='true'>
                {selectAllLabel && <span className='item-sizer'>{selectAllLabel}</span>}
                {selectableItems.map((item, index) => (
                    <span key={index} className='item-sizer'>
                        {['string', 'number'].includes(typeof item.label) ? item.label : String(item.value)}
                    </span>
                ))}
                {placeholder && <span className='trigger-sizer'>{placeholder}</span>}
                {selectedLabel && (
                    <>
                        <span className='trigger-sizer'>Alle {selectedLabel}</span>
                        <span className='trigger-sizer'>
                            {selectableItems.length} {selectedLabel}
                        </span>
                    </>
                )}
            </div>
        );
    }

    function renderError() {
        if (!error) return null;
        return <label className={classNames('bolmate-multi-select-error', errorClassName)}>{error}</label>;
    }

    function clickListener() {
        if (!hasHover(container.current)) setOpen(false);
    }

    useImperativeHandle(ref, () => ({onOpen, isOpen}));

    useEffect(() => {
        window.setTimeout(() => {
            if (open) document.addEventListener('click', clickListener);
            else document.removeEventListener('click', clickListener);
        });
    }, [open]);

    return (
        <div
            className={classNames(
                'bolmate-multi-select-container',
                disabled && 'disabled',
                open && 'open',
                !!error && 'error',
                className
            )}
            ref={container}
            onClick={onClickElement}
        >
            {renderLabel()}
            {renderSizer()}
            <div className='content-container' style={contentStyle}>
                {renderValue()}
                {renderArrow()}
            </div>
            {renderItems()}
            {renderError()}
        </div>
    );
}

export default forwardRef(BolmateMultiSelect) as <RT = string | number>(
    props: Props<RT> & {ref?: ForwardedRef<BolmateSelectRef>}
) => React.ReactElement | null;
